import type { Logger } from '@mimir/common/types/logger';
import type { AudioDecoder, AudioPacket, DecodedAudioFrame } from './demuxAndDecodeFrameProvider';
import type { TestPacket } from './testPacket';
import { decodeTestPacket } from './testPacket';
import { ceilFraction, frac, multiplyFractions } from '@mimir/common/util/integerMath';
import type { BufferCache } from '@mimir/cutter/frameProvider/refCountedBuffer';
import { invariant } from '@mimir/common/util/invariant';

export interface TestAudioDecoderSettings {
  // Discard samples from the first packet.
  emulateAac: boolean,

  allowAudioDiscontinuities: boolean,
}

export function createTestAudioDecoder(logger: Logger, bufferCache: BufferCache, settings: TestAudioDecoderSettings): AudioDecoder {
  return createTestAudioDecoderInternal(logger, bufferCache, settings);
}

// ---

function createTestAudioDecoderInternal(logger: Logger, bufferCache: BufferCache, settings: TestAudioDecoderSettings): AudioDecoder {
  const pendingPackets : AudioPacket[] = [];
  let flushed = false;
  let closed = false;
  let initPacketPushed = false;
  let expectedSampleOffset : number | null = null;
  return {
    reset(): void {
      logger.info('TestAudioDecoder: reset()');
      invariant(!closed, 'TestAudioDecoder.reset() called after close');
      pendingPackets.splice(0);
      flushed = false;
      initPacketPushed = false;
      expectedSampleOffset = null;
    },
    close(): void {
      this.reset();
      logger.info('TestAudioDecoder: close()');
      invariant(!closed, 'TestAudioDecoder.close() called after close');
      closed = true;
    },
    async push(packet: AudioPacket): Promise<void> {
      if (settings.emulateAac && !initPacketPushed) {
        initPacketPushed = true;
        return;
      }
      invariant(!closed, 'TestAudioDecoder.push called after close');
      pendingPackets.push(packet);
      if (pendingPackets.length > 10) {
        throw new Error('TestAudioDecoder.push: EAGAIN');
      }
      logger.debug(`push: pendingPackets=${pendingPackets.length}`);
    },
    async poll(): Promise<DecodedAudioFrame | null> {
      invariant(!closed, 'TestAudioDecoder.poll called after close');
      logger.debug('TestAudioDecoder: push()');
      // Emulate decode delay
      if (pendingPackets.length < 3 && !flushed) {
        logger.debug(`poll: emulating decoder delay: pendingPackets=${pendingPackets.length}`);
        return null;
      }

      const [packet] = pendingPackets.splice(0, 1);
      if (packet === undefined) {
        logger.debug('poll: no pending packets');
        return null;
      }

      const testPacket : TestPacket = decodeTestPacket(packet.payload);
      if (testPacket.header.type !== 'audio') {
        throw new Error('TestAudioDecoder.poll: Invalid packet');
      }
      const sampleRate = testPacket.header.sampleRate;
      const sampleOffset = ceilFraction(multiplyFractions(packet.pts, frac(sampleRate, 1)));

      const sampleCount = testPacket.header.sampleCount;

      if (expectedSampleOffset === null) {
        expectedSampleOffset = sampleOffset;
      }

      if (!settings.allowAudioDiscontinuities) {
        // Because the mpegts timebase may not have enough resolution to place
        // the audio packets we need a tolerance here on the sanity check.
        invariant(Math.abs(expectedSampleOffset - sampleOffset) <= 1, `Unexpected sample offset: ${expectedSampleOffset} != ${sampleOffset}`);
      }
      expectedSampleOffset = sampleOffset + sampleCount;


      logger.debug(`poll: returning audio frame: sampleOffset=${sampleOffset}, sampleCount=${sampleCount}`);
      return {
        audioFrame: {
          sampleOffset,
          sampleCount,
          channelCount: testPacket.header.channelCount,
          sampleRate,
          format: testPacket.header.format,
          buffer: bufferCache.allocateBuffer(
            testPacket.payload.length,
            (buffer: Uint8Array) => {
              buffer.set(testPacket.payload);
            }
          ),
        }
      };
    },
    async flush(): Promise<void> {
      invariant(!closed, 'TestAudioDecoder.flush called after close');
      flushed = true;
    }
  };
}
